import {
  RING_ID_DEVICE_SCHEMA,
  TIME_UNIT_OPTIONS,
  STUDY_STATUS,
  FOLLOW_ON_STUDY_TYPE,
  getContent,
  checkUsingHighSamplingRateStudy,
} from '@/entities/study';
import {
  InfoAlert,
  DatePickerInput,
  TextInput,
  ConfirmModal,
  CustomButton,
  CustomSelect,
} from '@/shared/ui/components';
import { arrowIcon } from '@/shared/ui/assets';

import { getDuration, zeroPad, addTimezoneToMoment } from '@/shared/lib';
import { Drawer, Space } from 'antd';
import dayjs from 'dayjs';
import dayjsBusinessDays from 'dayjs-business-days';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import { forwardRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { renderMessageBoxContent } from '../../../../lib/studyDetailsTab';
import { STUDY_OPTIONS } from '../../../../config/editStudyDetails';
import { getDisabledText } from '../../../../lib/editStudyDetails';
import { useEditStudyInfoDrawer } from './useEditStudyInfoDrawer';
import type { EditStudyInfoProps } from '../editStudyDetails.types';

dayjs.extend(dayjsBusinessDays);
dayjs.extend(utc);

/**
 * Drawer for editing a study's information — ring ID, study type, primary study type and duration among the other fields. Shows a skeleton while initiating, disables fields according to the ongoing, draft, completed and pending-device-command flags from useEditStudyInfoDrawer, warns when a follow-on study would be removed or when a high sampling rate caps the duration at 7 days, and applies changes only after the confirm modal triggers onSave.
 *
 * @param props - Study data, drawer open state and the remaining drawer props.
 * @param ref - Forwarded ref letting the study page open and drive the drawer.
 * @returns The study information drawer markup.
 */
const EditStudyInfoDrawer = forwardRef((props: EditStudyInfoProps, ref) => {
  const { data, open, ...params } = props;

  const {
    state,
    setState,
    hasDevicePendingCommand,
    studyOngoing,
    draftOrStartingStudy,
    isCompletedStudy,
    isFollowOnStudyType,
    isFirstHolterStudy,
    isSecondHolterStudy,
    isEndOfDurationStudy,
    reportDueDateMessage,
    isDisabledEdit,
    studyTypeOptions,
    isFOReportDueDate,
    handleChange,
    handleClickSave,
    handleOnClose,
    onSave,
  } = useEditStudyInfoDrawer(props, ref);

  return (
    <Drawer
      {...params}
      open={open}
      rootClassName="edit-study-drawer-wrapper"
      title={t`Study information`}
      aria-label={t`Study information`}
      onClose={handleOnClose}
      destroyOnClose
      maskClosable={false}
    >
      {state.isInitiating ? (
        <div className="edit-study-details">
          <Skeleton className="initiating-skeleton" />
        </div>
      ) : (
        <>
          <ConfirmModal
            title={t`Apply changes`}
            question={getContent()}
            isOpen={!!state.isOpenConfirmModal}
            leftButtonName={t`Cancel`}
            rightButtonName={t`Apply changes`}
            onClickLeftButton={() => {
              setState({ isOpenConfirmModal: false });
            }}
            onClickRightButton={() => {
              onSave();
            }}
            isSaving={state.isSaving}
          />

          <div className="edit-study-details">
            <div className="content-container">
              <div className="content">
                <div className="left">
                  <div className="info-alert">
                    {renderMessageBoxContent({
                      draftOrStartingStudy,
                      hasDevicePendingCommand,
                      pendingCommand: data?.pendingCommand?.command,
                      isCompletedStudy,
                    })}
                    {!isDisabledEdit &&
                      !isFirstHolterStudy &&
                      checkUsingHighSamplingRateStudy(data?.ecgDetail) && (
                        <InfoAlert
                          message={t`The device is operating at a high sampling rate, so the maximum study duration is 7 days.`}
                        />
                      )}
                  </div>
                </div>
                <div className="right">
                  {zeroPad(data?.device?.lastSync, 8) === RING_ID_DEVICE_SCHEMA && (
                    <div>
                      <TextInput
                        name="ringId"
                        type="number"
                        label={t`Ring ID`}
                        placeholder={t`Enter ring ID`}
                        value={state.ringId}
                        onChange={handleChange}
                        isError={!!state.listError?.ringId}
                        errorMessage={state.listError?.ringId}
                        disabled={!studyOngoing}
                      />
                    </div>
                  )}
                  <div>
                    <CustomSelect
                      name="studyType"
                      value={state.studyType}
                      size="middle"
                      title={t`Study type`}
                      aria-label={t`Study type`}
                      onChange={handleChange}
                      placeholder={t`Select study type`}
                      showSearch
                      allowClear={false}
                      options={studyTypeOptions}
                      errorMessage={
                        !_.isEmpty(data?.info?.followOnStudy) &&
                        state.studyType &&
                        state.studyType !== FOLLOW_ON_STUDY_TYPE &&
                        !isCompletedStudy &&
                        !data?.isTest
                          ? state.listError?.studyType ||
                            t`Caution: The follow-on study will be removed.`
                          : state.listError?.studyType
                      }
                      disabled={isDisabledEdit}
                      isError={
                        (!_.isEmpty(data?.info?.followOnStudy) &&
                          state.studyType &&
                          state.studyType !== FOLLOW_ON_STUDY_TYPE) ||
                        !!state.listError?.studyType
                      }
                      id="update-study-type"
                    />
                    {getDisabledText({
                      type: 'studyType',
                      isCompletedStudy,
                      isTestStudy: !!data?.isTest,
                    })}
                  </div>
                  {isFollowOnStudyType && (
                    <div>
                      <CustomSelect
                        name="primaryStudyType"
                        value={state.primaryStudyType}
                        size="middle"
                        title={t`Primary study type`}
                        aria-label={t`Primary study type`}
                        onChange={handleChange}
                        placeholder={t`Select study type`}
                        showSearch
                        allowClear={false}
                        options={_.filter(
                          studyTypeOptions,
                          (x) => x.value !== FOLLOW_ON_STUDY_TYPE,
                        )}
                        disabled={isDisabledEdit}
                        id="update-primary-study-type"
                      />
                    </div>
                  )}
                  <div>
                    {isFirstHolterStudy && !isDisabledEdit ? (
                      <Space.Compact>
                        <TextInput
                          name="duration"
                          type={isDisabledEdit ? 'text' : 'number'}
                          label={
                            isFollowOnStudyType ? t`Primary study duration` : t`Study duration`
                          }
                          placeholder={t`Enter study duration`}
                          value={state.duration ?? undefined}
                          onChange={handleChange}
                          isError={state.listError?.duration}
                          errorMessage={state.listError?.duration}
                          disabled={isDisabledEdit}
                          id="update-study-duration"
                        />
                        <CustomSelect
                          name="unitTime"
                          className="option-unit"
                          onChange={handleChange}
                          value={state.unitTime}
                          defaultValue="day"
                          options={TIME_UNIT_OPTIONS}
                          size="middle"
                          allowClear={false}
                          showSearch={false}
                          popupMatchSelectWidth={30}
                          suffixIcon={<img src={arrowIcon} alt="arrow icon" />}
                          disabled={isDisabledEdit}
                          aria-label={t`Study duration unit`}
                        />
                      </Space.Compact>
                    ) : (
                      <TextInput
                        name="duration"
                        type={isDisabledEdit ? 'text' : 'number'}
                        label={isFollowOnStudyType ? t`Primary study duration` : t`Study duration`}
                        placeholder={t`Enter study duration`}
                        suffix={isDisabledEdit ? undefined : 'day(s)'}
                        value={
                          isDisabledEdit
                            ? getDuration(
                                (state.duration || 0) * (state.unitTime === 'day' ? 1440 : 60),
                                'd[d] hh[h] mm[m]',
                                'h',
                              )
                            : (state.duration ?? undefined)
                        }
                        onChange={handleChange}
                        isError={state.listError?.duration}
                        errorMessage={state.listError?.duration}
                        disabled={isDisabledEdit}
                        id="update-study-duration"
                      />
                    )}
                    {getDisabledText({
                      type: 'duration',
                      isCompletedStudy,
                      isTestStudy: !!data?.isTest,
                    })}
                  </div>
                  {isFollowOnStudyType && (
                    <div className="FO-study">
                      <div>
                        <CustomSelect
                          name="followOnStudyStudyType"
                          value={state.followOnStudyStudyType}
                          size="middle"
                          title={t`Follow-on study type`}
                          aria-label={t`Follow-on study type`}
                          onChange={handleChange}
                          placeholder={t`Select study type`}
                          showSearch
                          allowClear={false}
                          options={STUDY_OPTIONS}
                          errorMessage={state.listError?.followOnStudyStudyType}
                          disabled={isDisabledEdit}
                          isError={!!state.listError?.followOnStudyStudyType}
                        />
                        {getDisabledText({
                          type: 'followOnStudyStudyType',
                          isCompletedStudy,
                          isTestStudy: !!data?.isTest,
                        })}
                      </div>
                      <div>
                        {isSecondHolterStudy && !isDisabledEdit ? (
                          <Space.Compact>
                            <TextInput
                              name="followOnStudyDuration"
                              type="number"
                              label={t`Follow-on study duration`}
                              placeholder={t`Enter study duration`}
                              value={state.followOnStudyDuration ?? undefined}
                              onChange={handleChange}
                              isError={state.listError?.followOnStudyDuration}
                              errorMessage={state.listError?.followOnStudyDuration}
                              id="update-fo-study-duration"
                            />
                            <CustomSelect
                              name="unitTimeFO"
                              className="option-unit"
                              onChange={handleChange}
                              value={state.unitTimeFO}
                              defaultValue="day"
                              options={TIME_UNIT_OPTIONS}
                              size="middle"
                              allowClear={false}
                              showSearch={false}
                              popupMatchSelectWidth={30}
                              suffixIcon={<img src={arrowIcon} alt="arrow icon" />}
                              aria-label={t`Follow-on study duration unit`}
                            />
                          </Space.Compact>
                        ) : (
                          <TextInput
                            name="followOnStudyDuration"
                            type="number"
                            label={t`Follow-on study duration`}
                            placeholder={t`Enter study duration`}
                            suffix={t`day(s)`}
                            value={state.followOnStudyDuration ?? undefined}
                            onChange={handleChange}
                            isError={state.listError?.followOnStudyDuration}
                            errorMessage={state.listError?.followOnStudyDuration}
                            disabled={isDisabledEdit}
                            id="update-fo-study-duration"
                          />
                        )}
                        {getDisabledText({
                          type: 'followOnStudyDuration',
                          isCompletedStudy,
                          isTestStudy: !!data?.isTest,
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <DatePickerInput
                      belowLimit={addTimezoneToMoment({
                        dateMoment: dayjs(data?.start || undefined)
                          .add(
                            (state.duration || 0) * 1440 +
                              (data?.status !== STUDY_STATUS.COMPLETED &&
                              data?.status !== STUDY_STATUS.DONE &&
                              !isEndOfDurationStudy
                                ? (state.followOnStudyDuration || 0) * 1440
                                : 0),
                            'minute',
                          )
                          .endOf('day'),
                      })?.valueOf()}
                      onChange={(value: string) => handleChange(isFOReportDueDate(), value)}
                      name={isFOReportDueDate()}
                      label={
                        data?.isDeviceAssignmentPending || data?.isBiofluxDirect
                          ? t`Patient return visit (optional)`
                          : t`Patient return visit`
                      }
                      placeholder="MM/DD/YYYY"
                      value={state[isFOReportDueDate()] ? dayjs(state[isFOReportDueDate()]) : null}
                      disabled={data?.isTest}
                      isError={!!state.listError?.[isFOReportDueDate()]}
                      errorMessage={state.listError?.[isFOReportDueDate()]}
                      isWarning={!!reportDueDateMessage}
                      warningMessage={reportDueDateMessage}
                      allowClear={false}
                      id="update-patient-return-visit"
                    />
                    {getDisabledText({
                      type: 'reportDueDate',
                      isCompletedStudy,
                      isTestStudy: !!data?.isTest,
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="edit-study-button-container">
              <CustomButton
                id="cancel-btn"
                type="outline"
                style="neutral"
                size="middle"
                onClick={handleOnClose}
              >
                {t`Cancel`}
              </CustomButton>
              <CustomButton
                id="save-btn"
                type="primary"
                style="default"
                size="middle"
                onClick={handleClickSave}
              >
                {t`Save`}
              </CustomButton>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
});

export default EditStudyInfoDrawer;
